import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../../components/Layout';
import { apiFetch, apiBlobUrl } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import ApiInspector from '../../components/ApiInspector';
import { API_DOCS } from './ParametriReportPage.api';

interface ReportSettings {
  id: number;
  societaId: number;
  ragioneSociale: string | null;
  partitaIva: string | null;
  indirizzo: string | null;
  logoUrl: string | null;
  footerLegale: string | null;
  mittenteNome: string | null;
  mittenteEmail: string | null;
}

export default function ParametriReportPage() {
  const { societaId, pronta } = useFiltroSocieta();
  const { can } = usePermesso();
  const canEdit = can('impostazioni.parametri_report', 'edit');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'err'; testo: string } | null>(null);

  const [ragioneSociale, setRagioneSociale] = useState('');
  const [partitaIva, setPartitaIva] = useState('');
  const [indirizzo, setIndirizzo] = useState('');
  const [footerLegale, setFooterLegale] = useState('');
  const [mittenteNome, setMittenteNome] = useState('');
  const [mittenteEmail, setMittenteEmail] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Il logo sta su un bucket privato: va recuperato con il token e trasformato
  // in object URL, perche' <img src> non puo' inviare l'header Authorization.
  useEffect(() => {
    if (!logoUrl) {
      setLogoSrc(null);
      return;
    }
    let annullato = false;
    let creato: string | null = null;
    // @api GET /api/report-settings/logo?societaId= -> binary (image/*)
    apiBlobUrl(logoUrl)
      .then(url => {
        if (annullato) { URL.revokeObjectURL(url); return; }
        creato = url;
        setLogoSrc(url);
      })
      .catch(() => { if (!annullato) setLogoSrc(null); });
    return () => {
      annullato = true;
      if (creato) URL.revokeObjectURL(creato);
    };
  }, [logoUrl]);

  const carica = useCallback(async () => {
    if (!societaId) return;
    setLoading(true);
    setMsg(null);
    try {
      // @api GET /api/report-settings?societaId= -> {"success":true,"data":null|{...}}
      const r = await apiFetch<{ success: boolean; data: ReportSettings | null }>(`/api/report-settings?societaId=${societaId}`);
      const d = r.data;
      setRagioneSociale(d?.ragioneSociale || '');
      setPartitaIva(d?.partitaIva || '');
      setIndirizzo(d?.indirizzo || '');
      setFooterLegale(d?.footerLegale || '');
      setMittenteNome(d?.mittenteNome || '');
      setMittenteEmail(d?.mittenteEmail || '');
      setLogoUrl(d?.logoUrl || null);
    } catch {
      setMsg({ tipo: 'err', testo: 'Errore nel caricamento dei parametri' });
    } finally {
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => {
    if (pronta && societaId) carica();
  }, [pronta, societaId, carica]);

  const salva = async () => {
    if (!societaId) return;
    setSaving(true);
    setMsg(null);
    try {
      // @api PUT /api/report-settings?societaId= {"ragioneSociale":"string","partitaIva":"string","indirizzo":"string","footerLegale":"string","mittenteNome":"string","mittenteEmail":"string"} -> {"success":true,"data":{...}}
      await apiFetch(`/api/report-settings?societaId=${societaId}`, {
        method: 'PUT',
        body: JSON.stringify({ ragioneSociale, partitaIva, indirizzo, footerLegale, mittenteNome, mittenteEmail }),
      });
      setMsg({ tipo: 'ok', testo: 'Parametri salvati con successo' });
    } catch {
      setMsg({ tipo: 'err', testo: 'Errore nel salvataggio' });
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file: File) => {
    if (!societaId) return;
    setUploadingLogo(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('societaId', societaId);
      fd.append('file', file);
      // @api POST /api/report-settings/upload-logo FormData{societaId,file} -> {"success":true,"logoUrl":"string"}
      const r = await apiFetch<{ success: boolean; logoUrl: string | null; warning?: string }>('/api/report-settings/upload-logo', {
        method: 'POST',
        body: fd,
      });
      if (r.logoUrl) {
        setLogoUrl(r.logoUrl);
        setMsg({ tipo: 'ok', testo: 'Logo caricato' });
      } else if (r.warning) {
        setMsg({ tipo: 'ok', testo: r.warning });
      }
    } catch {
      setMsg({ tipo: 'err', testo: 'Errore nel caricamento del logo' });
    } finally {
      setUploadingLogo(false);
    }
  };

  if (!pronta) {
    return (
      <Layout>
        <div className="bop-page-header">
          <h1 className="bop-page-title">Parametri Report</h1>
        </div>
        <div className="bop-card">
          <div className="bop-empty-state">Seleziona una società per visualizzare i parametri</div>
        </div>
        <ApiInspector docs={API_DOCS} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bop-page-header">
        <h1 className="bop-page-title">Parametri Report</h1>
        {canEdit && (
          <button className="bop-btn-primary" onClick={salva} disabled={saving || loading}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
        )}
      </div>

      {msg && (
        <div className={`bop-badge ${msg.tipo === 'ok' ? 'bop-badge-success' : 'bop-badge-danger'}`}>
          {msg.testo}
        </div>
      )}

      {loading ? (
        <div className="bop-card">
          <div className="bop-empty-state">Caricamento...</div>
        </div>
      ) : (
        <>
          <div className="bop-card">
            <div className="bop-section-label">Dati società</div>
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">Ragione sociale</label>
                <input className="bop-input" value={ragioneSociale} onChange={e => setRagioneSociale(e.target.value)} disabled={!canEdit} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Partita IVA</label>
                <input className="bop-input" value={partitaIva} onChange={e => setPartitaIva(e.target.value)} disabled={!canEdit} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Indirizzo</label>
                <input className="bop-input" value={indirizzo} onChange={e => setIndirizzo(e.target.value)} disabled={!canEdit} />
              </div>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Footer legale</label>
              <textarea className="bop-input" rows={3} value={footerLegale} onChange={e => setFooterLegale(e.target.value)} disabled={!canEdit} />
            </div>

            <div className="bop-section-divider" />
            <div className="bop-section-label">Mittente email predefinito</div>
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">Nome mittente</label>
                <input className="bop-input" value={mittenteNome} onChange={e => setMittenteNome(e.target.value)} disabled={!canEdit} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Email mittente</label>
                <input className="bop-input" type="email" value={mittenteEmail} onChange={e => setMittenteEmail(e.target.value)} disabled={!canEdit} />
              </div>
            </div>

            <div className="bop-section-divider" />
            <div className="bop-section-label">Logo società</div>

            {logoSrc ? (
              <div className="bop-flex">
                <img src={logoSrc} alt="Logo società" className="bop-logo-preview" />
                {canEdit && (
                  <button className="bop-btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={uploadingLogo}>
                    {uploadingLogo ? 'Caricamento...' : 'Cambia logo'}
                  </button>
                )}
              </div>
            ) : logoUrl ? (
              <div className="bop-empty-state">Caricamento logo...</div>
            ) : (
              canEdit && (
                <div className="bop-upload-zone" onClick={() => fileInputRef.current?.click()}>
                  <div className="bop-upload-zone__title">{uploadingLogo ? 'Caricamento...' : 'Clicca per caricare il logo'}</div>
                  <div className="bop-upload-zone__sub">Immagine PNG, JPG o SVG — max 2 MB</div>
                </div>
              )
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="bop-file-input-hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) uploadLogo(f);
                e.target.value = '';
              }}
            />
          </div>
        </>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
